import React from'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import classnames from 'classnames';
import 'moment/locale/en-gb';
import {
    get as _get,
    isString as _isString,
} from 'lodash';
import 'react-datepicker/dist/react-datepicker.css';

class Datepicker extends React.Component {
    static propTypes = {
        error: PropTypes.bool,
        disabled: PropTypes.bool,
        loading: PropTypes.bool,
        defaultValue: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object,
        ]),
        configuration: PropTypes.object,
        isReduxComponent: PropTypes.bool,
        name: PropTypes.string,
        onBlur: PropTypes.func.isRequired,
        onChange: PropTypes.func.isRequired,
        onChangeDate: PropTypes.func,
        selected: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object,
        ]),
        timestamp: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        placeholderText: PropTypes.string,
        className: PropTypes.string,
        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object,
        ]),
        restorePreviousValue: PropTypes.bool,
        meta: PropTypes.shape({
            submitting: PropTypes.bool.isRequired,
        }),
    };

    static defaultProps = {
        configuration: {},
        isReduxComponent: false,
        restorePreviousValue: false,
    };

    constructor (props) {
        super(props);

        let timeFormat = _get(props, 'configuration.timeFormat', 'HH:mm:ss');
        const format = props.configuration.dateFormat || (props.configuration.showTimeSelect
            ? `YYYY-MM-DD ${timeFormat}`
            : 'YYYY-MM-DD'
        );

        this.state = {
            disabled: null,
            prefilled: false,
            date: null,
            value: null,
            wrongFormatError: false,
            format: this.reformatDateFormat(format),
            momentFormat: format,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.meta.submitting) {
            return null;
        }

        let newState = null;

        if (nextProps.selected !== undefined
            && prevState.timestamp !== nextProps.timestamp
            && (nextProps.selected !== prevState.selected)) {
            newState = {
                ...(prevState || newState),
                timestamp: nextProps.timestamp,
            };
        }

        if (
            (!prevState.prefilled && nextProps.defaultValue)
            || (nextProps.disabled !== prevState.disabled)
            || (nextProps.timestamp !== prevState.timestamp)
        ) {
            if (nextProps.defaultValue) {
                let date = moment(nextProps.defaultValue).format(prevState.momentFormat),
                    value = (nextProps.isReduxComponent) ? nextProps.defaultValue : date;

                if (!nextProps.isReduxComponent
                    && nextProps.value !== nextProps.defaultValue
                    && prevState.selected !== undefined
                    && !nextProps.restorePreviousValue
                ) {
                    value = date = (nextProps.value)
                        ? moment(nextProps.value).format(prevState.momentFormat)
                        : null;
                }

                newState = {
                    ...newState,
                    disabled: nextProps.disabled,
                    date: moment(nextProps.defaultValue),
                    selected: date,
                    value: value ? new Date(value) : null,
                    prefilled: !nextProps.isReduxComponent,
                    timestamp: nextProps.timestamp,
                };
            } else if (
                !nextProps.defaultValue
                && (nextProps.disabled !== prevState.disabled || nextProps.timestamp !== prevState.timestamp)
                && nextProps.configuration.updateOnExternalChange
            ) {
                newState = {
                    ...newState,
                    disabled: nextProps.disabled,
                    date: null,
                    selected: null,
                    value: null,
                    prefilled: !nextProps.isReduxComponent,
                    timestamp: nextProps.timestamp,
                };
            } else {
                newState = {
                    ...newState,
                    prefilled: true,
                };
            }
        }

        if (nextProps.isReduxComponent && !nextProps.value) {
            newState = {
                ...newState,
                date: null,
                selected: null,
            };
        }

        return newState;
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.value !== this.props.value && this.props.isReduxComponent) {
            this.handleChange(this.props.value);
        }

        if (this.state.value && this.state.selected !== prevState.selected) {
            this.props.onChange(moment(this.state.value).format(this.state.momentFormat));
        }

        if (this.state.timestamp !== prevState.timestamp
            && this.props.selected && this.props.selected !== prevState.selected) {
            this.handleChange(this.props.selected);
        }
    }

    handleChange = (date) => {
        let dateObject = date,
            formattedDate = date;

        if (_isString(date)) {
            dateObject = moment(date);
            formattedDate = date;
        } else if (null !== date) {
            formattedDate = date ? moment(date).format(this.state.momentFormat) : null;
        }

        if (this.props.configuration.onChange) {
            this.props.configuration.onChange(moment(date), this.props.name);
        }

        this.props.onChange(formattedDate);
        this.props.onBlur();

        if (this.props.onChangeDate) {
            this.props.onChangeDate(formattedDate);
        }

        this.setState(() => ({
            date: dateObject,
            value: formattedDate ? new Date(formattedDate) : null,
            selected: new Date(formattedDate),
        }));
    };

    handleChangeRaw = (event) => {
        const date = event.target.value;
        const parsingFormat = this.props.configuration?.strictParsing
            ? this.state.momentFormat
            : this.state.format;
        const parsedDate = moment(date, parsingFormat, true);
        const filterDate = this.props.configuration?.filterDate;

        if (!parsedDate.isValid()) {
            return;
        }

        if ('function' === typeof filterDate && !filterDate(parsedDate.toDate())) {
            return;
        }

        this.handleChange(event.target.value);
    };

    reformatDateFormat = (format) => {
        return format.replace('DD', 'dd')
            .replace('YYYY', 'yyyy')
            .replace('YY', 'yy')
            .replace('D', 'd');
    };

    parseDatepickerConfiguration = () => {
        const data = this.props.configuration,
            dataFields = ['minDate', 'maxDate', 'minTime', 'maxTime'];

        dataFields.forEach((field) => {
            const fieldValue = _get(data, field, null);

            if (fieldValue) {
                data[field] = new Date(fieldValue);
            }
        });

        return data;
    };

    render() {
        return (
            <div className={classnames('datepicker-wrapper ui input small',
                { 'error': this.props.error, 'loading': this.props.loading })}>
                <DatePicker
                    { ...this.parseDatepickerConfiguration() }
                    error={this.props.error}
                    name={this.props.name}
                    selected={this.state.value}
                    value={this.state.value}
                    onChange={this.handleChange}
                    onChangeRaw={this.handleChangeRaw}
                    disabled={this.props.disabled}
                    dateFormat={this.state.format}
                    placeholderText={this.props.placeholderText}
                    className={this.props.className}
                />
                {this.props.loading && <i aria-hidden='true' className='spinner icon'/>}
            </div>
        );
    }
}

export default Datepicker;
